import { apiClient } from './api'

export interface YardPhotoAnalysis {
  summary?: string
  observations?: string[]
  concerns?: string[]
  hiveCountEstimate?: number | null
}

export interface YardPhotoComparison {
  previousPhotoId?: string
  previousTakenAt?: string
  daysBetween?: number
  summary?: string
  changes?: string[]
}

export interface YardPhoto {
  id: string
  yardId: string
  userName?: string
  imageUrl: string
  takenAt: string
  analysisStatus: 'pending' | 'done' | 'failed'
  analysisError?: string
  analysis?: YardPhotoAnalysis
  comparison?: YardPhotoComparison
  createdAt?: string
}

/**
 * Gets the photo gallery + journal for a yard (newest first)
 * @param yardId - Yard ID
 * @returns Promise with array of yard photos
 */
export function getYardPhotos(yardId: string) {
  return apiClient.get<YardPhoto[]>(`/yards/${yardId}/photos`)
}

/**
 * Uploads a photo of the yard site; the server stores it and runs AI analysis
 * @param yardId - Yard ID
 * @param file - Image blob (ideally resized client-side)
 * @param fileName - File name for the multipart part
 * @param language - App language code for the AI conclusions
 * @returns Promise with the created photo
 */
export function uploadYardPhoto(yardId: string, file: Blob, fileName: string, language?: string) {
  const form = new FormData()
  form.append('photo', file, fileName)
  if (language) form.append('language', language)
  return apiClient.post<YardPhoto>(`/yards/${yardId}/photos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
}

/**
 * Re-runs AI analysis for a yard photo
 * @param yardId - Yard ID
 * @param photoId - Photo ID
 * @returns Promise with the updated photo
 */
export function reanalyzeYardPhoto(yardId: string, photoId: string) {
  return apiClient.post<YardPhoto>(`/yards/${yardId}/photos/${photoId}/analyze`)
}

/**
 * Deletes a yard photo
 * @param yardId - Yard ID
 * @param photoId - Photo ID
 * @returns Promise with deletion result
 */
export function deleteYardPhoto(yardId: string, photoId: string) {
  return apiClient.delete(`/yards/${yardId}/photos/${photoId}`)
}

/**
 * Downscales an image in the browser before upload (max dimension, JPEG).
 * Falls back to the original file if the canvas pipeline fails.
 * @param file - Original image file
 * @param maxDim - Max width/height in pixels
 * @param quality - JPEG quality (0-1)
 * @returns Promise with blob + file name to upload
 */
export function resizeImageFile(
  file: File,
  maxDim = 1600,
  quality = 0.85
): Promise<{ blob: Blob; fileName: string }> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      try {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
        const width = Math.max(1, Math.round(img.width * scale))
        const height = Math.max(1, Math.round(img.height * scale))
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve({ blob: file, fileName: file.name })
          return
        }
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          blob => {
            if (blob) {
              resolve({ blob, fileName: file.name.replace(/\.[^.]+$/, '') + '.jpg' })
            } else {
              resolve({ blob: file, fileName: file.name })
            }
          },
          'image/jpeg',
          quality
        )
      } catch {
        resolve({ blob: file, fileName: file.name })
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({ blob: file, fileName: file.name })
    }
    img.src = url
  })
}
